import React, { useState, useEffect } from 'react';
import { appDeveloperService } from '../../lib/services/appstore/index.js';
import { useTranslation } from '../../lib/i18n';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import './DeveloperPortal.css';

const DeveloperPortal = ({ currentTenant, onBack, onTabChange }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [developer, setDeveloper] = useState(null);
  const [apps, setApps] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showAppForm, setShowAppForm] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  // Form states
  const [developerForm, setDeveloperForm] = useState({
    developer_name: '',
    developer_email: '',
    company_name: '',
    developer_type: 'individual'
  });

  const [appForm, setAppForm] = useState({
    app_name: '',
    app_description: '',
    app_category: 'math',
    app_subcategory: '',
    app_version: '1.0.0',
    pricing_model: 'token',
    base_token_cost: 1,
    app_features: [],
    target_audience: ['students'],
    grade_levels: ['primary'],
    languages: ['en']
  });

  useEffect(() => {
    loadDeveloperData();
  }, [currentTenant]);

  const loadDeveloperData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to get existing developer by email (you might want to use actual user email)
      const testEmail = 'developer@example.com';
      const existingDeveloper = await appDeveloperService.getDeveloperByEmail(testEmail, currentTenant);
      
      if (existingDeveloper) {
        setDeveloper(existingDeveloper);
        await loadDeveloperApps(existingDeveloper.id);
        await loadAnalytics(existingDeveloper.id);
      } else {
        setShowRegistration(true);
      }
    } catch (error) {
      console.error('Error loading developer data:', error);
      setError('Failed to load developer data');
      setShowRegistration(true);
    } finally {
      setLoading(false);
    }
  };

  const loadDeveloperApps = async (developerId) => {
    try {
      const appsData = await appDeveloperService.getDeveloperApps(developerId, currentTenant);
      setApps(appsData);
    } catch (error) {
      console.error('Error loading apps:', error);
      setError('Failed to load apps');
    }
  };

  const loadAnalytics = async (developerId) => {
    try {
      const analyticsData = await appDeveloperService.getDeveloperAnalytics(developerId, currentTenant);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleDeveloperRegistration = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const newDeveloper = await appDeveloperService.registerDeveloper(developerForm, currentTenant);
      setDeveloper(newDeveloper);
      setShowRegistration(false);
      setActiveTab('dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAppSubmission = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const newApp = await appDeveloperService.submitApp(appForm, developer.id, currentTenant);
      setApps([newApp, ...apps]);
      setShowAppForm(false);
      setAppForm({
        app_name: '',
        app_description: '',
        app_category: 'math',
        app_subcategory: '',
        app_version: '1.0.0',
        pricing_model: 'token',
        base_token_cost: 1,
        app_features: [],
        target_audience: ['students'],
        grade_levels: ['primary'],
        languages: ['en']
      });
    } catch (error) {
      console.error('App submission error:', error);
      setError('App submission failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAppUpdate = async (appId, updateData) => {
    try {
      const updatedApp = await appDeveloperService.updateApp(appId, updateData, developer.id, currentTenant);
      setApps(apps.map(app => app.id === appId ? updatedApp : app));
      setSelectedApp(null);
    } catch (error) {
      console.error('App update error:', error);
      setError('App update failed: ' + error.message);
    }
  };

  const handleAppDelete = async (appId) => {
    if (window.confirm('Are you sure you want to delete this app?')) {
      try {
        await appDeveloperService.deleteApp(appId, developer.id, currentTenant);
        setApps(apps.filter(app => app.id !== appId));
      } catch (error) {
        console.error('App deletion error:', error);
        setError('App deletion failed: ' + error.message);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'submitted': return '#f59e0b';
      case 'draft': return '#6b7280';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="developer-portal">
        <div className="loading">Loading Developer Portal...</div>
      </div>
    );
  }

  if (showRegistration) {
    return (
      <div className="developer-portal">
        <div className="portal-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h1>Developer Registration</h1>
        </div>
        
        <div className="registration-form">
          <h2>Join MyClass AI Platform as a Developer</h2>
          <p>Create your developer account to start building and publishing AI learning apps.</p>
          
          <form onSubmit={handleDeveloperRegistration}>
            <div className="form-group">
              <label>Developer Name *</label>
              <input
                type="text"
                value={developerForm.developer_name}
                onChange={(e) => setDeveloperForm({...developerForm, developer_name: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={developerForm.developer_email}
                onChange={(e) => setDeveloperForm({...developerForm, developer_email: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={developerForm.company_name}
                onChange={(e) => setDeveloperForm({...developerForm, company_name: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <label>Developer Type</label>
              <select
                value={developerForm.developer_type}
                onChange={(e) => setDeveloperForm({...developerForm, developer_type: e.target.value})}
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Registering...' : 'Register as Developer'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="developer-portal">
      <div className="portal-header">
        <button onClick={onBack} className="back-button">← {t('common.back')}</button>
        <h1>{t('developerPortal.title')}</h1>
        <LanguageSwitcher />
        <div className="developer-info">
          <span>Welcome, {developer?.developer_name}</span>
          <span className={`status ${developer?.verification_status}`}>
            {developer?.verification_status}
          </span>
        </div>
      </div>

      <div className="portal-tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 {t('developerPortal.dashboard')}
        </button>
        <button
          className={activeTab === 'apps' ? 'active' : ''}
          onClick={() => setActiveTab('apps')}
        >
          📱 {t('developerPortal.myApps')}
        </button>
        <button
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => setActiveTab('analytics')}
        >
          📈 {t('developerPortal.analytics')}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {activeTab === 'dashboard' && (
        <div className="dashboard-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Apps</h3>
              <p className="stat-number">{analytics?.totalApps || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Active Apps</h3>
              <p className="stat-number">{analytics?.activeApps || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Total Downloads</h3>
              <p className="stat-number">{analytics?.totalDownloads || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Total Revenue</h3>
              <p className="stat-number">${analytics?.totalRevenue?.toFixed(2) || '0.00'}</p>
            </div>
          </div>

          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <button onClick={() => setShowAppForm(true)} className="action-button">
              ➕ Submit New App
            </button>
            <button onClick={() => setActiveTab('apps')} className="action-button">
              📱 Manage Apps
            </button>
            <button onClick={() => setActiveTab('analytics')} className="action-button">
              📈 View Analytics
            </button>
          </div>
        </div>
      )}

      {activeTab === 'apps' && (
        <div className="apps-content">
          <div className="apps-header">
            <h3>My Applications</h3>
            <button onClick={() => setShowAppForm(true)} className="add-app-button">
              ➕ Add New App
            </button>
          </div>

          <div className="apps-grid">
            {apps.map(app => (
              <div key={app.id} className="app-card">
                <div className="app-header">
                  <h4>{app.app_name}</h4>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(app.app_status) }}
                  >
                    {app.app_status}
                  </span>
                </div>
                <p className="app-description">{app.app_description}</p>
                <div className="app-stats">
                  <span>📥 {app.total_downloads || 0} downloads</span>
                  <span>⭐ {app.rating?.toFixed(1) || '0.0'}</span>
                  <span>💰 ${app.total_revenue?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="app-actions">
                  <button onClick={() => setSelectedApp(app)}>Edit</button>
                  <button onClick={() => handleAppDelete(app.id)} className="delete">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="analytics-content">
          <h3>Analytics Overview</h3>
          <div className="analytics-grid">
            <div className="analytics-card">
              <h4>Performance Summary</h4>
              <div className="metric">
                <span>Average Rating:</span>
                <span>{analytics?.averageRating?.toFixed(1) || '0.0'} ⭐</span>
              </div>
              <div className="metric">
                <span>Pending Reviews:</span>
                <span>{analytics?.pendingApps || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAppForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{selectedApp ? 'Edit App' : 'Submit New App'}</h3>
              <button onClick={() => {
                setShowAppForm(false);
                setSelectedApp(null);
              }}>×</button>
            </div>
            
            <form onSubmit={handleAppSubmission}>
              <div className="form-group">
                <label>App Name *</label>
                <input
                  type="text"
                  value={appForm.app_name}
                  onChange={(e) => setAppForm({...appForm, app_name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={appForm.app_description}
                  onChange={(e) => setAppForm({...appForm, app_description: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={appForm.app_category}
                    onChange={(e) => setAppForm({...appForm, app_category: e.target.value})}
                  >
                    <option value="math">Mathematics</option>
                    <option value="english">English</option>
                    <option value="science">Science</option>
                    <option value="coding">Coding</option>
                    <option value="creativity">Creativity</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Version</label>
                  <input
                    type="text"
                    value={appForm.app_version}
                    onChange={(e) => setAppForm({...appForm, app_version: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Pricing Model</label>
                  <select
                    value={appForm.pricing_model}
                    onChange={(e) => setAppForm({...appForm, pricing_model: e.target.value})}
                  >
                    <option value="token">Token-based</option>
                    <option value="subscription">Subscription</option>
                    <option value="freemium">Freemium</option>
                    <option value="one_time">One-time</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Token Cost</label>
                  <input
                    type="number"
                    value={appForm.base_token_cost}
                    onChange={(e) => setAppForm({...appForm, base_token_cost: parseInt(e.target.value)})}
                    min="1"
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowAppForm(false);
                  setSelectedApp(null);
                }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Submitting...' : (selectedApp ? 'Update App' : 'Submit App')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperPortal;
